/**
 * A new retainer — FR-G6, T-3.5. Split out of `Retainers.tsx` and laid out as
 * the Branches dialog in Wave 15D (C14 §10.6 item 7; T-6.16). Behaviour
 * unchanged.
 *
 * **"Prepare a draft" and "Issue it automatically" are spelled out, not
 * abbreviated.** One of them puts a numbered tax invoice under the firm's own
 * GSTIN into the world at two in the morning with nobody attached to it. The
 * labels come from the API client so the two halves cannot describe the same
 * switch differently, and `draft` is the default everywhere.
 *
 * **The cadence and the start date cannot be changed afterwards, and the form
 * says so before they are set.** Every invoice a schedule has raised is keyed
 * on a period derived from those two, so moving either would make the next run
 * re-raise periods it has already billed. The way to change them is to end this
 * schedule and start another, which is also what a firm means when it says its
 * retainer changed.
 */

import { Alert, Button, Dialog, Input, Select } from '@fiveox/ui'
import { useState } from 'react'

import { FORM_GRID, FormRow } from '../../components/page'
import { ApiError } from '../../lib/api'
import {
  type BillingVocabulary,
  CADENCE_LABELS,
  ISSUE_MODE_LABELS,
  type RetainerCadence,
  billing,
} from '../../lib/api/billing'
import { DialogFooter } from './shared'

const CADENCES: RetainerCadence[] = ['monthly', 'quarterly', 'half_yearly', 'annual']

export function RetainerDialog({
  vocab,
  book,
  onClose,
  onCreated,
}: {
  vocab: BillingVocabulary | null
  /** Client id → name, the firm's active clients. */
  book: Record<string, string>
  onClose: () => void
  onCreated: () => void
}) {
  const today = new Date().toISOString().slice(0, 10)
  const [draft, setDraft] = useState({
    client_id: '',
    description: '',
    amount: '',
    cadence: 'monthly' as RetainerCadence,
    start_date: today,
    end_date: '',
    gst_treatment: 'taxable' as 'taxable' | 'exempt',
    gst_rate: '18',
    issue_mode: 'draft' as 'draft' | 'issue',
  })
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    setSaving(true)
    setError(null)
    try {
      await billing.createRetainer({
        client_id: draft.client_id,
        description: draft.description.trim(),
        amount: draft.amount.trim(),
        cadence: draft.cadence,
        start_date: draft.start_date,
        end_date: draft.end_date || null,
        gst_treatment: draft.gst_treatment,
        // An exempt supply carries no rate and the server refuses one that
        // does — cleared here so a firm that switches to exempt does not have
        // to also clear a field it can no longer see.
        gst_rate: draft.gst_treatment === 'exempt' ? null : draft.gst_rate,
        issue_mode: draft.issue_mode,
      })
      onCreated()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The retainer could not be created.')
      setSaving(false)
    }
  }

  const why = !draft.client_id
    ? 'Choose the client it bills.'
    : !draft.description.trim()
      ? 'Say what it is for — it is printed on every invoice.'
      : !draft.amount.trim()
        ? 'Type the amount before tax.'
        : undefined

  return (
    <Dialog open onClose={onClose} title="New retainer" error={error} width="lg">
      <div className={FORM_GRID}>
        <FormRow label="Client" htmlFor="retainer-client">
          <Select
            id="retainer-client"
            value={draft.client_id}
            onChange={(e) => setDraft({ ...draft, client_id: e.target.value })}
          >
            <option value="">Choose a client…</option>
            {Object.entries(book).map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </Select>
        </FormRow>
        <FormRow label="What it is for" htmlFor="retainer-description">
          <Input
            id="retainer-description"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            hint="Printed on every invoice this schedule raises."
          />
        </FormRow>
        <FormRow label="Amount" htmlFor="retainer-amount">
          <Input
            id="retainer-amount"
            value={draft.amount}
            onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
            inputMode="decimal"
            hint="Before tax."
          />
        </FormRow>
        <FormRow label="Every" htmlFor="retainer-cadence">
          <Select
            id="retainer-cadence"
            value={draft.cadence}
            onChange={(e) => setDraft({ ...draft, cadence: e.target.value as RetainerCadence })}
          >
            {(vocab?.retainer_cadences ?? CADENCES).map((c) => (
              <option key={c} value={c}>
                {CADENCE_LABELS[c]}
              </option>
            ))}
          </Select>
        </FormRow>
        <FormRow label="Starts" htmlFor="retainer-start">
          <Input
            id="retainer-start"
            type="date"
            value={draft.start_date}
            onChange={(e) => setDraft({ ...draft, start_date: e.target.value })}
            hint="Sets the day of the month and where the periods fall. A date in the past does not bill the past — the first invoice is for the period running now."
          />
        </FormRow>
        <FormRow label="Ends" htmlFor="retainer-end" optional>
          <Input
            id="retainer-end"
            type="date"
            value={draft.end_date}
            onChange={(e) => setDraft({ ...draft, end_date: e.target.value })}
            hint="Blank means until somebody stops it, which is what most retainers are."
          />
        </FormRow>
        <FormRow label="GST" htmlFor="retainer-gst">
          <Select
            id="retainer-gst"
            value={draft.gst_treatment}
            onChange={(e) =>
              setDraft({ ...draft, gst_treatment: e.target.value as 'taxable' | 'exempt' })
            }
          >
            <option value="taxable">Taxable</option>
            <option value="exempt">Exempt</option>
          </Select>
        </FormRow>
        <FormRow label="Rate %" htmlFor="retainer-rate">
          <Input
            id="retainer-rate"
            value={draft.gst_rate}
            onChange={(e) => setDraft({ ...draft, gst_rate: e.target.value })}
            inputMode="decimal"
            disabled={draft.gst_treatment === 'exempt'}
            hint={draft.gst_treatment === 'exempt' ? 'An exempt supply carries no rate.' : undefined}
          />
        </FormRow>
        <FormRow label="At the run" htmlFor="retainer-issue-mode">
          <Select
            id="retainer-issue-mode"
            value={draft.issue_mode}
            onChange={(e) =>
              setDraft({ ...draft, issue_mode: e.target.value as 'draft' | 'issue' })
            }
          >
            <option value="draft">{ISSUE_MODE_LABELS.draft}</option>
            <option value="issue">{ISSUE_MODE_LABELS.issue}</option>
          </Select>
        </FormRow>
      </div>

      {draft.issue_mode === 'issue' && (
        <Alert tone="warning" className="mt-4">
          Issuing takes the next number in your series and posts to the client’s ledger, with
          nobody there to check it. It cannot be undone — a mistake is corrected by a credit note.
        </Alert>
      )}

      <p className="text-muted-foreground mt-4 text-xs">
        The cadence and the start date cannot be changed later: every invoice this schedule raises
        is keyed on a period counted from them, and moving either would make the next run bill
        periods it has already billed. To change them, end the schedule and start another.
      </p>

      <DialogFooter onCancel={onClose} busy={saving}>
        <span title={why}>
          <Button onClick={() => void submit()} disabled={why !== undefined} loading={saving}>
            Create
          </Button>
        </span>
      </DialogFooter>
    </Dialog>
  )
}
